import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { AgentChatMessageFragment, AgentChatMessageRole } from '@treams/treams-network';
import { Button, FlexGap, Heading3, Paragraph, chatSlideIn, tokens } from '@treams/treams-ui';

import { matchAgentChatSuggestions } from '../agentChatSuggestions';
import { useAIAssistanceApproval } from '../useAIAssistanceApproval';
import { AgentChatAIApprovalCard } from './AgentChatAIApprovalCard';
import { AgentChatMessageGroup, AgentChatTypingIndicator } from './AgentChatBubble';
import { AgentChatCostSummary } from './AgentChatCostSummary';
import { SUGGESTIONS, SuggestionButton, SuggestionButtonGroup } from './AgentChatEmpty';

function isAiRole(role: AgentChatMessageRole): boolean {
	return role === AgentChatMessageRole.Assistant || role === AgentChatMessageRole.Tool;
}

function isVisibleChatMessage(message: AgentChatMessageFragment): boolean {
	return message.role !== AgentChatMessageRole.Tool && !!message.content;
}

const MessagesContainer = styled(Box)<{ $compact?: boolean }>`
	flex: 1;
	overflow-y: auto;
	overscroll-behavior: contain;
	min-height: 0;
	padding: ${(props) =>
		props.$compact
			? `${tokens.padding.container}px ${tokens.padding.container}px 0`
			: `${tokens.padding.page}px ${tokens.padding.page}px 0`};
`;

const MessageColumn = styled(Box)`
	width: 100%;
	max-width: 720px;
	margin: 0 auto;
`;

const FooterSection = styled(FlexGap).attrs({
	gap: tokens.gap.section,
	flexDirection: 'column',
})`
	width: 100%;
	animation: 0.35s cubic-bezier(0, 1, 0, 1) ${chatSlideIn};
	animation-fill-mode: backwards;
	text-align: left;
`;

export function AgentChatMessageList(props: {
	messages: AgentChatMessageFragment[];
	isLoading?: boolean;
	isStreaming: boolean;
	error?: string;
	compact?: boolean;
	onSelectSuggestion: (message: string, subjectID?: string) => void;
	onStartOver: () => void;
	estimatedCost?: number;
}) {
	const { t } = useTranslation();

	const { needsApproval } = useAIAssistanceApproval();

	const toolCalls = useMemo(() => {
		return props.messages.flatMap((message) => message.toolCalls ?? []);
	}, [props.messages]);

	const matchedSuggestions = useMemo(() => {
		const userMessages = props.messages
			.filter((message) => message.role === AgentChatMessageRole.User)
			?.map((message) => message.content)
			.filter((content) => content !== null && content !== undefined);

		if (!userMessages.length) {
			return [];
		}

		return matchAgentChatSuggestions(userMessages, t);
	}, [props.messages, t]);

	const remainingSuggestions = useMemo(() => {
		if (!matchedSuggestions.length) {
			return [];
		}

		const matchedSuggestionKeys = new Set(matchedSuggestions.map((suggestion) => suggestion.key));
		const isMe = matchedSuggestions.some((suggestion) => suggestion.isMe);
		const name = matchedSuggestions.find((suggestion) => !suggestion.isMe)?.name;

		return SUGGESTIONS.filter((suggestion) => !matchedSuggestionKeys.has(suggestion.key)).map(
			(suggestion) => {
				const key = isMe ? `${suggestion.key}Me` : suggestion.key;

				return { ...suggestion, key, text: t(key, { name }) };
			},
		);
	}, [matchedSuggestions, t]);

	// reflexbox's `Box` does not forward refs, so we ref a plain inner div and
	// walk up from it to the nearest ancestor that actually overflows (the
	// scroll container), instead of assuming which element scrolls.
	const contentRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = useCallback(() => {
		let container = contentRef.current?.parentElement;

		while (container && container.scrollHeight <= container.clientHeight) {
			container = container.parentElement;
		}

		if (container) container.scrollTop = container.scrollHeight;
	}, []);

	useEffect(() => {
		scrollToBottom();
	}, [props.messages, scrollToBottom]);

	useEffect(() => {
		if (!props.isStreaming) return undefined;

		// Brute-force pin while the answer streams in: keep forcing the scroll
		// to the maximum so no reflow can leave the view mid-thread.
		const interval = window.setInterval(scrollToBottom, 100);

		return () => window.clearInterval(interval);
	}, [props.isStreaming, scrollToBottom]);

	useEffect(() => {
		const content = contentRef.current;

		if (!content) return undefined;

		// Charts and grids finish loading (and grow the thread) shortly after
		// streaming ends; follow any content growth back down to the bottom.
		const observer = new ResizeObserver(scrollToBottom);

		observer.observe(content);

		return () => observer.disconnect();
	}, [scrollToBottom]);

	const showFooter = props.messages.length > 0 && !props.isStreaming;

	return (
		<MessagesContainer $compact={props.compact}>
			<MessageColumn>
				<div ref={contentRef}>
					{props.messages.map((message, index) => {
						// Skip hidden tool rows when comparing speakers, so consecutive
						// assistant bubbles get half the space (mt=2 vs mt=3).
						const previousVisible = props.messages
							.slice(0, index)
							.findLast((candidate) => isVisibleChatMessage(candidate));
						const isSameSpeaker =
							!!previousVisible && isAiRole(previousVisible.role) === isAiRole(message.role);

						return (
							<AgentChatMessageGroup
								// Keyed by position, not id: streamed messages carry synthetic
								// ids that are swapped for database ids after the post-stream
								// refetch, and an id key would re-mount the whole bubble (and
								// its charts) on that swap.
								key={`${message.role}-${index}`}
								message={message}
								toolCalls={toolCalls}
								streaming={props.isStreaming && index === props.messages.length - 1}
								mt={isSameSpeaker ? 2 : 3}
							/>
						);
					})}

					{props.isStreaming &&
						props.messages[props.messages.length - 1].role !== AgentChatMessageRole.Assistant && (
							<AgentChatTypingIndicator
								mt={
									isAiRole(
										props.messages.findLast((message) => isVisibleChatMessage(message))?.role ??
											props.messages[props.messages.length - 1].role,
									)
										? 2
										: 3
								}
							/>
						)}

					{props.error && (
						<Paragraph textAlign="center" style={{ color: tokens.error.text }}>
							{props.error}
						</Paragraph>
					)}

					{showFooter && (
						<FooterSection py={4}>
							{needsApproval && (
								<Flex justifyContent="center">
									<AgentChatAIApprovalCard />
								</Flex>
							)}

							{remainingSuggestions.length > 0 && (
								<Box>
									<Heading3 mb={`${tokens.gap.list}px`}>{t('aiAgent.otherQuestions')}</Heading3>
									<SuggestionButtonGroup $faded={needsApproval}>
										{remainingSuggestions.map((suggestion) => (
											<SuggestionButton
												key={suggestion.key}
												suggestion={suggestion}
												disabled={needsApproval}
												onClick={() => props.onSelectSuggestion(suggestion.text)}
											/>
										))}
									</SuggestionButtonGroup>
								</Box>
							)}

							{!props.compact && (
								<Button
									alignSelf="start"
									type="secondary"
									title={t('aiAgent.startOver')}
									onClick={props.onStartOver}
								/>
							)}

							<AgentChatCostSummary estimatedCost={props.estimatedCost} />
						</FooterSection>
					)}
				</div>
			</MessageColumn>
		</MessagesContainer>
	);
}
